import { Gift, RefreshCw, Search, ShieldCheck, Users } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../api";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  Pagination,
} from "../components/design-system";
import { WechatPaymentOrders } from "../components/wechat-payment-orders";
import { AutoDormCheckGrantDialog } from "../components/auto-dorm-check-grant-dialog";
import { useResource } from "../hooks/use-resource";
import { courseGrabTaskLabels, courseGrabTaskTone } from "../course-grab";
import { formatDateTime } from "../utils";
import { GlobalSwitchRow, InlineQuotaValue } from "./auto-dorm-check";

export function CourseGrabPage() {
  const settings = useResource(() => api.courseGrabSettings());
  const [tab, setTab] = useState<"users" | "orders">("users");
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<"all" | "paid" | "quota">("all");
  const [draft, setDraft] = useState("");
  const [query, setQuery] = useState("");
  const [grantOpen, setGrantOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [maxUsersDraft, setMaxUsersDraft] = useState("");
  const [editingLimit, setEditingLimit] = useState(false);
  useEffect(() => {
    if (!editingLimit)
      setMaxUsersDraft(
        settings.data?.maxUsers == null ? "" : String(settings.data.maxUsers),
      );
  }, [settings.data?.maxUsers, editingLimit]);
  const inFlight = useRef(false);
  const selectionKey = JSON.stringify([page, query, filter]);
  const users = useResource(
    async () => ({
      selectionKey,
      result: await api.courseGrabUsers({
        page,
        pageSize: 8,
        q: query,
        filter,
      }),
    }),
    [page, query, filter],
    15000,
  );
  const result =
    users.data?.selectionKey === selectionKey ? users.data.result : null;
  const refreshAll = async () => {
    await Promise.all([settings.refresh(), users.refresh()]);
  };
  async function updateSettings(patch: {
    enabled?: boolean;
    maxUsers?: number | null;
  }) {
    if (!settings.data || inFlight.current) return;
    inFlight.current = true;
    setSaving(true);
    try {
      settings.setData(
        await api.updateCourseGrabSettings({
          ...patch,
          version: settings.data.version,
        }),
      );
      await users.refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "保存失败");
      await settings.refresh();
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  }
  async function saveLimit() {
    const value = maxUsersDraft.trim();
    const maximum = value === "" ? null : Number(value);
    if (
      maximum !== null &&
      (!/^\d+$/.test(value) ||
        !Number.isSafeInteger(maximum) ||
        maximum < 1 ||
        maximum > 4294967295)
    ) {
      toast.error("请输入正整数，或留空表示不限");
      setEditingLimit(false);
      return;
    }
    if (maximum !== (settings.data?.maxUsers ?? null))
      await updateSettings({ maxUsers: maximum });
    setEditingLimit(false);
  }
  return (
    <div className="auto-dorm-check-page">
      <header className="adc-page-header rise-in">
        <div className="min-w-0">
          <p className="adc-kicker">COURSE ASSISTANT</p>
          <h1>抢课助手（实验）</h1>
          <p className="adc-page-description">运行控制、抢课任务与用户次数</p>
        </div>
        <div className="adc-page-actions">
          <button
            type="button"
            className="adc-button adc-button-clay"
            onClick={() => setGrantOpen(true)}
          >
            <Gift size={14} />
            发放次数
          </button>
          <button
            type="button"
            className="adc-button adc-button-outline"
            disabled={settings.refreshing || users.refreshing}
            onClick={() => void refreshAll()}
          >
            <RefreshCw
              size={14}
              className={
                settings.refreshing || users.refreshing ? "animate-spin" : ""
              }
            />
            刷新
          </button>
        </div>
      </header>
      <nav
        className="adc-workspace-tabs rise-in rise-in-1"
        aria-label="抢课助手管理分区"
      >
        {(
          [
            { id: "users", label: "用户与任务" },
            { id: "orders", label: "订单" },
          ] as const
        ).map((item) => (
          <button
            key={item.id}
            type="button"
            aria-current={tab === item.id ? "page" : undefined}
            className={`adc-workspace-tab ${tab === item.id ? "is-active" : ""}`}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </nav>
      {tab === "users" ? (
        <div className="adc-workspace rise-in rise-in-2">
          {settings.error ? (
            <ErrorState
              error={settings.error}
              onRetry={() => void settings.refresh()}
            />
          ) : null}
          <section className="adc-card adc-runtime-panel">
            <div className="adc-card-toolbar">
              <div className="adc-toolbar-title">
                <span className="adc-toolbar-icon">
                  <ShieldCheck size={16} />
                </span>
                <div>
                  <h2>运行控制</h2>
                  <p>学生端入口与任务总开关</p>
                </div>
              </div>
              <span className="adc-muted-time">
                更新于 {formatDateTime(settings.data?.updatedAt)}
              </span>
            </div>
            {settings.loading && !settings.data ? (
              <LoadingState rows={2} />
            ) : (
              <div className="course-grab-runtime-controls">
                <GlobalSwitchRow
                  title="小程序入口"
                  description="关闭后暂停配置并保留偏好"
                  enabled={Boolean(settings.data?.enabled)}
                  disabled={!settings.data || saving || settings.refreshing}
                  onChange={(enabled) => void updateSettings({ enabled })}
                />
                <label className="adc-control-item course-grab-user-limit">
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-semibold">
                      最大使用人数
                    </span>
                    <span className="block text-xs text-ink-faint">
                      任意 20 分钟内
                    </span>
                  </span>
                  <input
                    type="number"
                    min={1}
                    max={4294967295}
                    step={1}
                    aria-label="最大使用人数"
                    placeholder="不限"
                    value={maxUsersDraft}
                    disabled={!settings.data || saving || settings.refreshing}
                    onFocus={() => setEditingLimit(true)}
                    onChange={(event) => setMaxUsersDraft(event.target.value)}
                    onBlur={() => void saveLimit()}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") event.currentTarget.blur();
                      if (event.key === "Escape") {
                        setMaxUsersDraft(
                          settings.data?.maxUsers == null
                            ? ""
                            : String(settings.data.maxUsers),
                        );
                        setEditingLimit(false);
                      }
                    }}
                  />
                </label>
              </div>
            )}
          </section>
          <section className="adc-card overflow-hidden">
            <div className="adc-card-toolbar adc-users-toolbar">
              <div className="adc-users-toolbar-main">
                <div className="adc-toolbar-title">
                  <span className="adc-toolbar-icon">
                    <Users size={16} />
                  </span>
                  <div>
                    <div className="flex items-baseline gap-2">
                      <h2>所有用户</h2>
                      <span className="adc-count">
                        {result?.pagination.total ?? "—"}
                      </span>
                    </div>
                    <p>抢课次数与最近任务</p>
                  </div>
                </div>
                <div
                  className="adc-user-filters"
                  role="tablist"
                  aria-label="用户筛选"
                >
                  {(
                    [
                      { value: "all", label: "全部" },
                      { value: "paid", label: "已购买" },
                      { value: "quota", label: "有额度" },
                    ] as const
                  ).map((item) => (
                    <button
                      key={item.value}
                      type="button"
                      role="tab"
                      aria-selected={filter === item.value}
                      className={filter === item.value ? "is-active" : ""}
                      onClick={() => {
                        setPage(1);
                        setFilter(item.value);
                      }}
                    >
                      {item.label}
                      {result ? <span>{result.facets[item.value]}</span> : null}
                    </button>
                  ))}
                </div>
              </div>
              <form
                className="adc-search"
                onSubmit={(e) => {
                  e.preventDefault();
                  setPage(1);
                  setQuery(draft.trim());
                }}
              >
                <Search size={14} aria-hidden />
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="搜索姓名、学号或班级…"
                  aria-label="搜索姓名、学号或班级"
                />
                <button type="submit" className="sr-only">
                  搜索
                </button>
              </form>
            </div>
            {users.error ? (
              <div className="p-4">
                <ErrorState
                  error={users.error}
                  onRetry={() => void users.refresh()}
                />
              </div>
            ) : null}
            {!result && !users.error ? (
              <LoadingState rows={8} />
            ) : result?.items.length ? (
              <>
                <div className="adc-user-table-scroll">
                  <div
                    className="adc-user-grid course-grab-user-grid adc-user-table-head"
                    aria-hidden
                  >
                    <span>用户</span>
                    <span>注册时间</span>
                    <span>抢课次数</span>
                    <span>购买状态</span>
                    <span>最近任务 · 近三次</span>
                  </div>
                  <div className="adc-user-table-body">
                    {result.items.map((user) => (
                      <div className="adc-user-row-shell" key={user.userId}>
                        <div className="adc-user-grid course-grab-user-grid adc-user-row">
                          <div className="adc-user-identity">
                            <span className="adc-avatar">
                              {(user.displayName || user.account).slice(0, 1)}
                            </span>
                            <div className="min-w-0">
                              <strong>
                                {user.displayName || user.account}
                              </strong>
                              <small>{user.studentNumber || "—"}</small>
                              {user.className ? (
                                <small>{user.className}</small>
                              ) : null}
                            </div>
                          </div>
                          <div className="adc-user-meta" data-label="注册时间">
                            <small>{formatDateTime(user.createdAt)}</small>
                          </div>
                          <div className="adc-user-meta" data-label="抢课次数">
                            <div className="adc-inline-quota">
                              <span className="adc-quota-slot">
                                <InlineQuotaValue
                                  user={{
                                    id: user.userId,
                                    name: user.displayName || user.account,
                                    studentNumber: user.studentNumber ?? null,
                                    account: user.account,
                                  }}
                                  kind="count"
                                  value={user.balance.remaining}
                                  maximum={1000}
                                  unit="次"
                                  onChanged={users.refresh}
                                  onSave={(target, reason, key) =>
                                    api.adjustCourseGrabUser(
                                      user.userId,
                                      {
                                        operation: "set",
                                        quotaAmount: target,
                                        reason,
                                      },
                                      key,
                                    )
                                  }
                                />
                              </span>
                            </div>
                            <small>
                              {user.balance.reserved} 次待执行 ·{" "}
                              {user.balance.consumed} 次已使用
                            </small>
                          </div>
                          <div className="adc-user-meta" data-label="购买状态">
                            <span
                              className={`adc-status-pill ${user.purchased ? "is-positive" : ""}`}
                            >
                              {user.purchased ? "已购买" : "未购买"}
                            </span>
                          </div>
                          <div
                            className="adc-recent-tasks"
                            data-label="最近任务 · 近三次"
                          >
                            {user.recentTasks.length ? (
                              user.recentTasks.map((task) => (
                                <div
                                  key={task.id}
                                  title={task.searchKeywords.join("、")}
                                >
                                  <div className="min-w-0">
                                    <strong className="block max-w-48 truncate text-xs font-medium">
                                      {task.searchKeywords.join("、")}
                                    </strong>
                                    <time>
                                      {formatDateTime(task.scheduledAt)}
                                    </time>
                                  </div>
                                  <span
                                    className={`adc-task-state is-${courseGrabTaskTone(task.state)}`}
                                  >
                                    <i />
                                    {courseGrabTaskLabels[task.state] ||
                                      "执行中"}
                                  </span>
                                </div>
                              ))
                            ) : (
                              <span className="adc-empty-location">
                                暂无任务
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <Pagination
                  page={result.pagination.page}
                  pages={Math.max(1, result.pagination.totalPages)}
                  total={result.pagination.total}
                  onChange={setPage}
                  className="adc-card-footer"
                />
              </>
            ) : result ? (
              <EmptyState
                title="没有匹配的用户"
                description="请调整搜索或筛选条件。"
              />
            ) : null}
          </section>
        </div>
      ) : (
        <div className="adc-workspace rise-in rise-in-2">
          <WechatPaymentOrders
            product="course-grab"
            onRefunded={() => void users.refresh()}
          />
        </div>
      )}
      <AutoDormCheckGrantDialog
        product="course-grab"
        open={grantOpen}
        plans={[]}
        onClose={() => setGrantOpen(false)}
        onGranted={async () => {
          await users.refresh();
        }}
      />
    </div>
  );
}
